/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Signup Landing Page - Account type selection
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Users, GraduationCap, ArrowRight, Check, Mail, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthLayout from '../components/auth/AuthLayout'
import MarketingPanel from '../components/auth/MarketingPanel'
import { useAuthStore } from '../stores/authStore'
import { getVisitorId } from '../services/campaignService'
import { createLogger } from '../utils/logger'

const logger = createLogger('SignupLanding')

const SignupLanding = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { code: pathCode } = useParams()
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const [configLoading, setConfigLoading] = useState(true)
  const [invitationLoading, setInvitationLoading] = useState(false)
  const { user, isAuthenticated, checkAuth } = useAuthStore()

  // Check if user has an invitation token or referral code
  const hasInvitationToken = searchParams.has('token')
  const invitationToken = searchParams.get('token')
  const referralCode = searchParams.get('ref') || pathCode
  const hasReferralCode = !!referralCode

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config/public')
        if (response.ok) {
          const config = await response.json()
          setRegistrationEnabled(config.registrationEnabled)
        }
      } catch (error) {
        // Default to showing signup options if config fetch fails
        logger.error('Failed to fetch config:', error)
      } finally {
        setConfigLoading(false)
      }
    }
    fetchConfig()
  }, [])

  // Record campaign hit on landing
  useEffect(() => {
    if (!referralCode) return

    const recordHit = async () => {
      try {
        const response = await fetch(`/api/campaigns/resolve/${encodeURIComponent(referralCode)}`)
        if (response.ok) {
          const data = await response.json()
          if (data.success && data.data?.valid && data.data?.type === 'campaign') {
            const visitorId = getVisitorId()
            fetch('/api/campaigns/hit', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                campaignCode: referralCode,
                visitorId,
                landingPage: window.location.pathname,
                queryParams: Object.fromEntries(new URLSearchParams(window.location.search))
              })
            }).catch(() => {}) // fire-and-forget
          }
        }
      } catch (err) {
        // Silently ignore - hit recording is non-critical
      }
    }
    recordHit()
  }, [referralCode])

  // If there's an invitation token, fetch the invitation and redirect to the appropriate signup page
  useEffect(() => {
    const checkInvitation = async () => {
      if (!invitationToken) return

      setInvitationLoading(true)
      try {
        const response = await fetch(`/api/invitations/${invitationToken}`)
        if (response.ok) {
          const data = await response.json()
          const invitation = data.invitation || data.data

          if (invitation && invitation.role) {
            // If user is already logged in, check if they can accept this invitation directly
            if (isAuthenticated && user) {
              const userEmail = user.email?.toLowerCase()
              const invitationEmail = invitation.email?.toLowerCase()

              if (userEmail === invitationEmail) {
                // Email matches! Accept invitation directly
                try {
                  const acceptResponse = await fetch(`/api/invitations/${invitationToken}/accept`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({})
                  })

                  if (acceptResponse.ok) {
                    await checkAuth() // Refresh user data
                    toast.success(
                      `You've joined ${invitation.organization?.name || 'the organization'}!`
                    )
                    navigate('/dashboard', { replace: true })
                    return
                  } else {
                    const errorData = await acceptResponse.json()
                    toast.error(errorData.message || 'Failed to accept invitation')
                    navigate('/dashboard', { replace: true })
                    return
                  }
                } catch (acceptError) {
                  logger.error('Error accepting invitation:', acceptError)
                  toast.error('Failed to accept invitation')
                  navigate('/dashboard', { replace: true })
                  return
                }
              } else {
                // Email mismatch - show error and redirect to dashboard
                toast.error(
                  `This invitation is for ${invitation.email}. You're logged in as ${user.email}.`
                )
                navigate('/dashboard', { replace: true })
                return
              }
            }

            // Not logged in - check if user already exists
            if (invitation.existingUser) {
              navigate(`/login?token=${invitationToken}`, { replace: true })
              return
            }
            // Redirect based on the invitation role for new users
            if (invitation.role === 'guardian') {
              navigate(`/register/guardian?token=${invitationToken}`, { replace: true })
              return
            } else if (invitation.role === 'student') {
              navigate(`/register/student?token=${invitationToken}`, { replace: true })
              return
            }
          }
        }
      } catch (error) {
        logger.error('Failed to fetch invitation:', error)
        // If we can't fetch the invitation, show the selection page (token will be passed along)
      }
      setInvitationLoading(false)
    }

    checkInvitation()
  }, [invitationToken, navigate, isAuthenticated, user, checkAuth])

  const accountTypes = [
    {
      type: 'student',
      icon: GraduationCap,
      title: 'Student Account',
      description: 'Create challenges, track progress, and ace the SAT',
      features: [
        'Unlimited practice challenges',
        'Detailed performance analytics',
        'Strategy-based learning',
        'Progress tracking'
      ],
      route: '/register/student',
      gradient: 'from-primary-600 to-primary-700',
      hoverGradient: 'from-primary-700 to-primary-800'
    },
    {
      type: 'guardian',
      icon: Users,
      title: 'Guardian Account',
      description: "Manage and monitor your children's SAT prep",
      features: [
        'Family account management',
        "View children's progress",
        'Manage subscriptions',
        'Access detailed reports'
      ],
      route: '/register/guardian',
      gradient: 'from-purple-600 to-purple-700',
      hoverGradient: 'from-purple-700 to-purple-800'
    }
  ]

  // Show loading while checking invitation
  if (invitationLoading || (hasInvitationToken && configLoading)) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Loading..."
        formSubtitle="Please wait while we verify your invitation"
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </AuthLayout>
    )
  }

  // Show registration disabled message if registration is disabled and no invitation token or referral code
  if (!configLoading && !registrationEnabled && !hasInvitationToken && !hasReferralCode) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Registration Unavailable"
        formSubtitle="Open registration is currently disabled"
      >
        <div className="space-y-6">
          {/* Disabled Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              New account registration requires an invitation. If you have received an invitation
              email, please use the link provided in that email to create your account.
            </p>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-200 text-left">
                  Check your email for an invitation link, or contact your organization
                  administrator to request access.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>

          {/* Sign In Link */}
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in to your account
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      marketingContent={<MarketingPanel />}
      formTitle="Get Started"
      formSubtitle="Choose your account type to begin"
    >
      <div className="space-y-6">
        {/* Account Type Cards */}
        <div className="space-y-4">
          {accountTypes.map((account, index) => {
            const Icon = account.icon

            return (
              <motion.button
                key={account.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => {
                  let targetRoute = account.route
                  const params = new URLSearchParams()
                  if (invitationToken) params.set('token', invitationToken)
                  if (referralCode) params.set('ref', referralCode)
                  if (params.toString()) targetRoute += `?${params.toString()}`
                  navigate(targetRoute)
                }}
                className="w-full group"
              >
                <div className="relative bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 transition-all duration-200 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-lg">
                  {/* Icon */}
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${account.gradient} rounded-lg mb-4 group-hover:scale-110 transition-transform duration-200`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  {/* Title and Description */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {account.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {account.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-2 mb-4">
                    {account.features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Arrow Icon */}
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <ArrowRight className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              </motion.button>
            )
          })}
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              Already have an account?
            </span>
          </div>
        </div>

        {/* Sign In Link */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            Sign in to your account
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </AuthLayout>
  )
}

export default SignupLanding
